// Epoch 19: Lead Generator Enhancements - Tier Range Editor Component
// File: src/components/admin/TierRangeEditor.tsx
// Purpose: Editor for tier score range configuration with validation

import React from 'react';

interface TierRangesData {
  tier_score_ranges: {
    [key: string]: {
      min: number;
      max: number;
    };
  };
}

interface TierRangeEditorProps {
  tierRanges: TierRangesData;
  onChange: (tierRanges: TierRangesData) => void;
}

const TIER_INFO = [
  {
    tier: '1',
    label: 'Tier 1 - Premium',
    description: 'Highest scoring leads (typically 90-100)',
    color: 'bg-green-50 border-green-200',
    dotColor: 'bg-green-500'
  },
  {
    tier: '2',
    label: 'Tier 2 - High',
    description: 'High scoring leads (typically 80-89)',
    color: 'bg-blue-50 border-blue-200',
    dotColor: 'bg-blue-500'
  },
  {
    tier: '3',
    label: 'Tier 3 - Good',
    description: 'Good scoring leads (typically 70-79)',
    color: 'bg-yellow-50 border-yellow-200',
    dotColor: 'bg-yellow-500'
  },
  {
    tier: '4',
    label: 'Tier 4 - Standard',
    description: 'Standard scoring leads (typically 60-69)',
    color: 'bg-orange-50 border-orange-200',
    dotColor: 'bg-orange-500'
  },
  {
    tier: '5',
    label: 'Tier 5 - Economy',
    description: 'Lower scoring leads (typically 0-59)',
    color: 'bg-gray-50 border-gray-200',
    dotColor: 'bg-gray-500'
  }
];

export default function TierRangeEditor({ tierRanges, onChange }: TierRangeEditorProps) {
  const handleRangeChange = (tier: string, field: 'min' | 'max', value: number) => {
    onChange({
      ...tierRanges,
      tier_score_ranges: {
        ...tierRanges.tier_score_ranges,
        [tier]: {
          ...tierRanges.tier_score_ranges[tier],
          [field]: value
        }
      }
    });
  };

  // Validation functions
  const hasOverlaps = () => {
    const ranges = Object.entries(tierRanges.tier_score_ranges);
    for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
        const [tierA, rangeA] = ranges[i];
        const [tierB, rangeB] = ranges[j];
        
        if (tierA === tierB) continue;
        
        // Check for overlap
        if (rangeA.min <= rangeB.max && rangeB.min <= rangeA.max) {
          return { tierA, tierB, rangeA, rangeB };
        }
      }
    }
    return null;
  };

  const hasGaps = () => {
    const sortedRanges = Object.entries(tierRanges.tier_score_ranges)
      .sort(([, a], [, b]) => a.min - b.min);
    
    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const currentRange = sortedRanges[i][1];
      const nextRange = sortedRanges[i + 1][1];
      
      if (currentRange.max + 1 < nextRange.min) {
        return {
          gap: { start: currentRange.max + 1, end: nextRange.min - 1 },
          between: [sortedRanges[i][0], sortedRanges[i + 1][0]]
        };
      }
    }
    
    // Check if ranges cover 0-100
    const minRange = Math.min(...sortedRanges.map(([, range]) => range.min));
    const maxRange = Math.max(...sortedRanges.map(([, range]) => range.max));
    
    if (minRange > 0) {
      return { gap: { start: 0, end: minRange - 1 }, between: ['start', sortedRanges[0][0]] };
    }
    
    if (maxRange < 100) {
      return { gap: { start: maxRange + 1, end: 100 }, between: [sortedRanges[sortedRanges.length - 1][0], 'end'] };
    }
    
    return null;
  };

  const hasInvalidRanges = () => {
    return Object.entries(tierRanges.tier_score_ranges).some(([, range]) => {
      return range.min > range.max || range.min < 0 || range.max > 100;
    });
  };

  const overlap = hasOverlaps();
  const gap = hasGaps();
  const invalidRanges = hasInvalidRanges();

  const getTierCount = (tier: string) => {
    const range = tierRanges.tier_score_ranges[tier];
    if (!range) return 0;
    
    // Simulate lead count for this range (this would come from actual data in production)
    const rangeSize = range.max - range.min + 1;
    const baseCount = Math.floor(rangeSize * 100); // Approximate leads per score point
    
    // Apply tier-based multipliers to simulate realistic distribution
    const multipliers = { '1': 0.5, '2': 0.7, '3': 1.2, '4': 1.0, '5': 0.8 };
    return Math.floor(baseCount * (multipliers[tier as keyof typeof multipliers] || 1));
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Lead Score Range Configuration</h3>
        <p className="text-sm text-gray-600">
          Define the score ranges that determine lead quality tiers. Ranges must cover 0-100 without gaps or overlaps.
        </p>
      </div>

      {/* Tier Range Configuration */}
      <div className="space-y-4">
        {TIER_INFO.map((tierInfo) => {
          const range = tierRanges.tier_score_ranges[tierInfo.tier];
          if (!range) return null;

          const estimatedCount = getTierCount(tierInfo.tier);
          const isInvalid = range.min > range.max || range.min < 0 || range.max > 100;

          return (
            <div
              key={tierInfo.tier}
              className={`border rounded-lg p-4 ${tierInfo.color} ${isInvalid ? 'border-red-300' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <div className={`w-3 h-3 rounded-full mr-3 ${tierInfo.dotColor}`}></div>
                    <h4 className="font-medium text-gray-900">{tierInfo.label}</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{tierInfo.description}</p>
                  
                  {/* Range Inputs */}
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">Min Score:</label>
                      <input
                        type="number"
                        value={range.min}
                        onChange={(e) => handleRangeChange(tierInfo.tier, 'min', parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        min="0"
                        max="100"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">Max Score:</label>
                      <input
                        type="number"
                        value={range.max}
                        onChange={(e) => handleRangeChange(tierInfo.tier, 'max', parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        min="0"
                        max="100"
                      />
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      Range: {range.max - range.min + 1} points
                    </div>
                  </div>

                  {/* Validation for this tier */}
                  {isInvalid && (
                    <div className="mt-2 text-sm text-red-600">
                      {range.min > range.max && "Min score cannot be greater than max score"}
                      {(range.min < 0 || range.max > 100) && "Scores must be between 0 and 100"}
                    </div>
                  )}
                </div>
                
                {/* Estimated Count */}
                <div className="text-right text-sm">
                  <div className="font-medium text-gray-700">Estimated Leads:</div>
                  <div className="text-lg font-bold text-gray-900">{estimatedCount.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">in this tier</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Visual Range Display */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Score Range Visualization</h4>
        
        {/* Score Scale */}
        <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
          {Object.entries(tierRanges.tier_score_ranges)
            .sort(([, a], [, b]) => a.min - b.min)
            .map(([tier, range]) => {
              const tierInfo = TIER_INFO.find(t => t.tier === tier);
              if (!tierInfo) return null;
              
              const left = range.min;
              const width = range.max - range.min + 1;
              
              return (
                <div
                  key={tier}
                  className={`absolute top-0 bottom-0 ${tierInfo.dotColor} flex items-center justify-center text-white text-xs font-medium`}
                  style={{
                    left: `${left}%`,
                    width: `${width}%`
                  }}
                >
                  T{tier}
                </div>
              );
            })}
        </div>
        
        {/* Scale Labels */}
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
          <span>100</span>
        </div>
      </div>

      {/* Validation Messages */}
      <div className="space-y-2">
        {overlap && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <div className="text-red-700 text-sm font-medium">Range Overlap Detected</div>
                <div className="text-red-600 text-xs">
                  Tier {overlap.tierA} ({overlap.rangeA.min}-{overlap.rangeA.max}) overlaps with 
                  Tier {overlap.tierB} ({overlap.rangeB.min}-{overlap.rangeB.max})
                </div>
              </div>
            </div>
          </div>
        )}

        {gap && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <div className="text-yellow-700 text-sm font-medium">Score Gap Detected</div>
                <div className="text-yellow-600 text-xs">
                  Scores {gap.gap.start}-{gap.gap.end} are not covered by any tier
                </div>
              </div>
            </div>
          </div>
        )}

        {!overlap && !gap && !invalidRanges && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-green-700 text-sm">All score ranges are valid with no gaps or overlaps</span>
            </div>
          </div>
        )}
      </div>

      {/* Quick Fix Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={() => {
            onChange({
              tier_score_ranges: {
                '1': { min: 90, max: 100 },
                '2': { min: 80, max: 89 },
                '3': { min: 70, max: 79 },
                '4': { min: 60, max: 69 },
                '5': { min: 0, max: 59 }
              }
            });
          }}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Reset to Defaults
        </button>
        
        <button
          onClick={() => {
            onChange({
              tier_score_ranges: {
                '1': { min: 80, max: 100 },
                '2': { min: 60, max: 79 },
                '3': { min: 40, max: 59 },
                '4': { min: 20, max: 39 },
                '5': { min: 0, max: 19 }
              }
            });
          }}
          className="px-3 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          Apply Equal Distribution
        </button>
      </div>

      {/* Best Practices */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">📊 Score Range Best Practices</h4>
        <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
          <li>Tier 1 should capture only the highest quality leads (typically 90-100 scores)</li>
          <li>Distribute ranges based on your actual lead score distribution data</li>
          <li>Ensure ranges cover all possible scores (0-100) without gaps</li>
          <li>Avoid overlapping ranges as they create tier assignment ambiguity</li>
          <li>Consider business value when setting tier boundaries</li>
          <li>Review and adjust ranges quarterly based on lead performance data</li>
        </ul>
      </div>
    </div>
  );
}